import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import './index.css'
import App from './App.tsx'
import { initI18n } from './i18n'
import { configureAxios } from './utils/axiosConfig'

// Initialize i18n before rendering
initI18n();

// Configure axios with interceptors
configureAxios();

// Define data types and their staleness configurations
const dataTypeConfigs = {
  // Real-time data that changes frequently
  realtime: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes (renamed from cacheTime in newer versions)
  },
  // Standard metrics that change somewhat frequently
  metrics: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes (renamed from cacheTime in newer versions)
  },
  // Historical data that rarely changes
  historical: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour (renamed from cacheTime in newer versions)
  },
  // Reference data that almost never changes
  reference: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours (renamed from cacheTime in newer versions)
  }
};

// Create a query cache with global error handling
const queryCache = new QueryCache({
  onError: (error, query) => {
    // Log all errors to console
    console.error(`Query error: ${error}`, query);

    // You could also send errors to an error tracking service here
  },
});

// Create a client with optimized configuration
const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      // Default to metrics configuration
      staleTime: dataTypeConfigs.metrics.staleTime,
      gcTime: dataTypeConfigs.metrics.gcTime, // renamed from cacheTime in newer versions

      // Stale-while-revalidate pattern
      // Don't refetch on window focus by default (can be overridden per query)
      refetchOnWindowFocus: false,

      // Always refetch when a component mounts if the data is stale
      refetchOnMount: 'always',

      // Refetch when the network reconnects
      refetchOnReconnect: true,

      // Retry failed queries
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      // Retry failed mutations
      retry: 1,
      retryDelay: 1000,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </StrictMode>,
)
